'use client';

import { ShareIos } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button, DialogTrigger } from 'react-aria-components';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon
} from 'react-share';
import { Stack } from 'ui';
import { Popover } from '~/components/Popover';
import { trackEvent } from '~/utils';
import styles from './ShareButton.module.css';

const SHARE_TRACKING_ID = 'share-button';

interface ShareButtonProps {
  quote?: string;
  iconOnly?: boolean;
}

export default function ShareButton({ quote, iconOnly = false }: ShareButtonProps) {
  const t = useTranslations();
  const resolvedQuote = quote ?? t('content.share_default_quote');
  const shareProviders = [
    { Component: EmailShareButton, name: 'Email', Icon: EmailIcon },
    { Component: WhatsappShareButton, name: 'WhatsApp', Icon: WhatsappIcon },
    { Component: FacebookShareButton, name: 'Facebook', Icon: FacebookIcon },
    { Component: TwitterShareButton, name: 'Twitter', Icon: XIcon },
    { Component: LinkedinShareButton, name: 'LinkedIn', Icon: LinkedinIcon },
    { Component: TelegramShareButton, name: 'Telegram', Icon: TelegramIcon },
    { Component: RedditShareButton, name: 'Reddit', Icon: RedditIcon }
  ];

  const [url, setUrl] = useState('');

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  const handlePress = () => {
    trackEvent(SHARE_TRACKING_ID, {
      event_category: 'click',
      event_label: SHARE_TRACKING_ID
    });
  };

  return (
    <DialogTrigger>
      <Button
        onPress={handlePress}
        className={styles.shareButton}
        aria-label={iconOnly ? t('common.share') : undefined}
      >
        <ShareIos decorative size={'large'} />
        {!iconOnly && t('common.share')}
      </Button>
      <Popover>
        <Stack>
          <h3 className="typography-title-6">{t('content.share_heading')}</h3>
          <ul>
            {shareProviders.map((Provider) => (
              <li key={Provider.name} className={styles.shareProvider}>
                <Provider.Component
                  url={url}
                  title={resolvedQuote}
                  resetButtonStyle={false}
                  className={styles.providerButton}
                  role="button"
                  tabIndex={0}
                >
                  <Provider.Icon size={32} round className={styles.providerIcon} />
                  <span>{Provider.name}</span>
                </Provider.Component>
              </li>
            ))}
          </ul>
        </Stack>
      </Popover>
    </DialogTrigger>
  );
}
